import React from "react";
import { Link } from "react-router-dom";
import { Smile, Sun, Users, ChefHat, Phone, Heart } from "lucide-react";
import { PlaceholderImage } from "../components/ui-kit";
import { useContact } from "../context/SiteSettingsContext";

const POINTS = [
  {
    icon: Sun,
    text: "We help young adults have good days, Monday to Friday.",
    img: "https://customer-assets.emergentagent.com/job_spire-support-hub/artifacts/gyxnf2xn_IMG-20260515-WA0049.jpg",
    alt: "Someone we support playing a lively ball game on an outdoor sports court.",
  },
  {
    icon: ChefHat,
    text: "We learn life skills, like cooking and getting around.",
    img: "https://customer-assets.emergentagent.com/job_spire-support-hub/artifacts/kq5fn77i_IMG-20260619-WA0004.jpg",
    alt: "A support worker helping someone we support measure baking ingredients during a cooking session.",
  },
  {
    icon: Users,
    text: "We make friends and do fun things together.",
    img: "https://customer-assets.emergentagent.com/job_spire-support-hub/artifacts/hjrnzf5p_IMG-20260619-WA0003.jpg",
    alt: "A group from Spire Inclusion enjoying a trip to the cinema together.",
  },
  {
    icon: Heart,
    text: "We help you feel safe, happy and listened to.",
    img: "https://customer-assets.emergentagent.com/job_spire-support-hub/artifacts/u44hwfev_IMG-20260626-WA0004.jpg",
    alt: "A group of people we support and a support worker enjoying a meal out together at a restaurant.",
  },
];

export default function EasyRead() {
  const contact = useContact();
  return (
    <div className="bg-stone-50">
      <header className="bg-white border-b border-stone-200" data-testid="easy-read-header">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <p className="text-lg font-semibold text-orange-700">Easy Read</p>
          <h1 className="mt-3 text-4xl sm:text-5xl font-bold text-stone-900">
            About Spire Inclusion
          </h1>
          <p className="mt-5 text-xl sm:text-2xl text-stone-800 leading-relaxed">
            This page uses short sentences and pictures. It is easy to read.
          </p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        <div className="space-y-10">
          <section
            className="rounded-3xl bg-white border border-stone-200 p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-6"
            data-testid="easy-read-intro"
          >
            <span className="flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl bg-orange-50 text-orange-700">
              <Smile className="h-10 w-10" aria-hidden="true" />
            </span>
            <p className="text-2xl text-stone-900 leading-relaxed">
              Spire Inclusion is a place for young adults. We are open in the daytime.
            </p>
          </section>

          {POINTS.map((p, i) => (
            <section
              key={i}
              className="rounded-3xl bg-white border border-stone-200 p-6 sm:p-8"
              data-testid={`easy-read-point-${i}`}
            >
              <div className="grid items-center gap-6 sm:grid-cols-2">
                <PlaceholderImage
                  src={p.img}
                  alt={p.alt}
                  aspect="aspect-[4/3]"
                  className={i % 2 === 1 ? "sm:order-2" : ""}
                />
                <div className="flex items-start gap-5">
                  <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-orange-50 text-orange-700">
                    <p.icon className="h-8 w-8" aria-hidden="true" />
                  </span>
                  <p className="text-2xl text-stone-900 leading-relaxed">{p.text}</p>
                </div>
              </div>
            </section>
          ))}

          <section
            className="rounded-3xl bg-orange-50 border border-orange-200 p-6 sm:p-8"
            data-testid="easy-read-contact"
          >
            <div className="flex items-start gap-5">
              <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-orange-600 text-white">
                <Phone className="h-8 w-8" aria-hidden="true" />
              </span>
              <div>
                <p className="text-2xl text-stone-900 leading-relaxed">
                  Do you want to talk to us? You can call us or email us.
                </p>
                <p className="mt-4 text-xl font-semibold text-stone-900">Phone: {contact.phone}</p>
                <p className="mt-1 text-xl font-semibold text-stone-900">Email: {contact.email}</p>
                <Link
                  to="/contact"
                  className="mt-6 inline-flex items-center justify-center px-8 py-4 rounded-xl bg-orange-600 text-white font-semibold text-xl hover:bg-orange-700"
                  data-testid="easy-read-contact-button"
                >
                  Go to the contact page
                </Link>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
